import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { useFlash } from "../lib/flash";
import { apiError } from "../lib/auth";
import { fmtShortDate, fmtDateTime } from "../lib/format";
import { Pill, EventText } from "../components/bits";
import {
  X, KeyRound, Ban, Check, Receipt, Trash2, Search, Store, CreditCard,
  Clock, Gift, AlertTriangle, PauseCircle, Unplug, TrendingUp, ShoppingBag,
  DollarSign, Tag, StickyNote, CalendarClock, ChevronRight, Bell,
  LogIn, Package, Users, Activity, FileText, Lock, Copy, CalendarPlus,
} from "lucide-react";
import { HISTORY_LABEL } from "../lib/billing";
import OwnerInvoices from "../components/OwnerInvoices";
import SellerSettings from "../components/SellerSettings";
import LandingSettings from "../components/LandingSettings";
import OwnerTestimonials from "../components/OwnerTestimonials";
import OwnerSupport from "../components/OwnerSupport";

const ACCESS_PILL = {
  free: ["picked_up", "Gratis"],
  active: ["picked_up", "Betaler"],
  trial: ["pending_confirm", "Prøve"],
  past_due: ["no_show", "Betaling mangler"],
  suspended: ["cancelled", "Suspenderet"],
  expired: ["cancelled", "Udløbet"],
  canceled: ["cancelled", "Opsagt"],
  cancelled: ["cancelled", "Stoppet"],
};

const CHANNEL_LABEL = {
  sms: "SMS",
  post: "Facebook-opslag",
  post_edit: "Opdatér opslag",
  comment_reply: "Offentligt svar",
  private_reply: "Privat besked",
  like: "Like",
};

// [key, label, icon, color-var]
const KPIS = [
  ["stores", "butikker i alt", Store, "var(--green)"],
  ["paying", "betalende", CreditCard, "var(--green)"],
  ["trial", "i prøveperiode", Clock, "var(--amber)"],
  ["free", "på gratis", Gift, "var(--muted)"],
  ["past_due", "betaling mangler", AlertTriangle, "var(--red)"],
  ["unpaid", "ubetalte fakturaer", FileText, "var(--amber)"],
  ["overdue", "forfaldne fakturaer", AlertTriangle, "var(--red)"],
  ["locked", "låst adgang", Lock, "var(--red)"],
  ["fb_broken", "Facebook afbrudt", Unplug, "var(--red)"],
  ["suspended", "suspenderet", PauseCircle, "var(--red)"],
];

const FILTERS = [
  ["all", "Alle"],
  ["active", "Betaler"],
  ["trial", "Prøve"],
  ["free", "Gratis"],
  ["past_due", "Betaling mangler"],
  ["expired", "Udløbet"],
  ["unpaid", "Ubetalt faktura"],
  ["pending", "Faktura på vej"],
  ["cancelled", "Stoppet"],
  ["suspended", "Suspenderet"],
];

const INFO_ROWS = [
  ["company", "Firmanavn"], ["cvr", "CVR"], ["address", "Adresse"], ["zip_city", "Postnr. og by"],
  ["email", "Faktura-e-mail"], ["ean", "EAN"], ["reference", "Reference"],
];

function BillingInfo({ info, pending }) {
  const flash = useFlash();
  if (!info) return <div className="muted" style={{ fontSize: 14 }}>Ingen faktureringsoplysninger endnu.</div>;
  const copy = (t) => navigator.clipboard?.writeText(t).then(() => flash("Kopieret."), () => flash("Kunne ikke kopiere.", "error"));
  const all = INFO_ROWS.filter(([k]) => info[k]).map(([k, l]) => `${l}: ${info[k]}`).join("\n");
  return (
    <div className="bi-box" data-testid="owner-billing-info">
      {pending && <div className="bi-req"><Pill status="pending" label="Faktura på vej" /> afventer bankoplysninger</div>}
      {INFO_ROWS.filter(([k]) => info[k]).map(([k, l]) => (
        <div className="bi-row" key={k}>
          <span className="muted">{l}</span>
          <b>{info[k]}</b>
          <button type="button" className="ck-close" aria-label={`Kopiér ${l}`} onClick={() => copy(info[k])} data-testid={`owner-copy-${k}`}>
            <Copy size={15} />
          </button>
        </div>
      ))}
      <button type="button" className="btn small" onClick={() => copy(all)} data-testid="owner-copy-all">
        <Copy size={15} /> Kopiér alt
      </button>
    </div>
  );
}

const KPI_FILTER = { past_due: "past_due", unpaid: "unpaid", overdue: "unpaid" };

function iconTint(color) {
  return { color, background: `color-mix(in srgb, ${color} 16%, transparent)` };
}

function daysSince(iso) {
  if (!iso) return null;
  return Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 86400000));
}

function agingLabel(days) {
  if (days === 0) return "i dag";
  if (days === 1) return "1 dag";
  return `${days} dage`;
}

function Sparkline({ data, color = "var(--green)" }) {
  if (!data || data.length < 2) return null;
  const vals = data.map((d) => d.orders);
  const max = Math.max(1, ...vals);
  const w = 120, h = 36, pad = 2;
  const step = w / (vals.length - 1);
  const pts = vals.map((v, i) => `${(i * step).toFixed(1)},${(h - pad - (v / max) * (h - pad * 2)).toFixed(1)}`);
  const line = pts.join(" ");
  const area = `0,${h} ${line} ${w},${h}`;
  return (
    <svg className="spark" width={w} height={h} viewBox={`0 0 ${w} ${h}`} aria-hidden="true" data-testid="owner-trend-spark">
      <polyline points={area} fill={`color-mix(in srgb, ${color} 14%, transparent)`} stroke="none" />
      <polyline points={line} fill="none" stroke={color} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
}

function ErrorsDialog({ store, onClose }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    api.get(`/owner/stores/${store.id}/errors`)
      .then((r) => setData(r.data))
      .catch(() => setErr("Kunne ikke hente fejl."));
  }, [store.id]);

  const render = (list, isFail) =>
    list.map((o) => (
      <div className={"err-line" + (isFail ? " fail" : "")} key={o.id} data-testid={`owner-err-${o.id}`}>
        <div className="top">
          <b style={{ fontSize: 13 }}>{CHANNEL_LABEL[o.channel] || o.channel}</b>
          <small className="muted">{fmtDateTime(o.created_at)}</small>
        </div>
        {o.error && <div className="msg">{o.error}</div>}
        {o.text && <div className="txt">“{o.text.length > 160 ? o.text.slice(0, 160) + "…" : o.text}”</div>}
      </div>
    ));

  return (
    <div className="ck-overlay" role="dialog" aria-modal="true" data-testid="owner-errors-dialog">
      <div className="ck-modal" style={{ maxWidth: 560 }}>
        <div className="ck-head">
          <div className="ck-brand"><b>Fejl & aktivitet · {store.name}</b></div>
          <button className="ck-close" onClick={onClose} aria-label="Luk" data-testid="owner-errors-close">
            <X size={18} />
          </button>
        </div>
        <div className="ck-body" style={{ display: "grid", gap: 16, maxHeight: "68vh", overflowY: "auto" }}>
          {err && <div className="notice error">{err}</div>}
          {!data && !err && <div className="muted">Indlæser…</div>}
          {data && (
            <>
              <div>
                <label style={{ fontWeight: 700, fontSize: 14, display: "block", marginBottom: 8 }}>
                  Mislykkede handlinger ({data.errors.length})
                </label>
                {data.errors.length ? render(data.errors, true) : (
                  <div className="muted" style={{ fontSize: 14 }}>Ingen fejl registreret. 🎉</div>
                )}
              </div>
              <div>
                <label style={{ fontWeight: 700, fontSize: 14, display: "block", marginBottom: 8 }}>
                  Seneste aktivitet
                </label>
                {data.recent.length ? render(data.recent, false) : (
                  <div className="muted" style={{ fontSize: 14 }}>Ingen live-aktivitet endnu.</div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function StoreProfileDialog({ store, onClose, onImpersonate, onManage }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    api.get(`/owner/stores/${store.id}`)
      .then((r) => setData(r.data))
      .catch(() => setErr("Kunne ikke hente butiksprofil."));
  }, [store.id]);

  const kr = (n) => (n ?? 0).toLocaleString("da-DK") + " kr.";

  return (
    <div className="ck-overlay" role="dialog" aria-modal="true" data-testid="owner-profile-dialog">
      <div className="ck-modal" style={{ maxWidth: 680 }}>
        <div className="ck-head">
          <div className="ck-brand"><b>{store.name}</b></div>
          <button className="ck-close" onClick={onClose} aria-label="Luk" data-testid="owner-profile-close">
            <X size={18} />
          </button>
        </div>
        <div className="ck-body" style={{ display: "grid", gap: 18, maxHeight: "74vh", overflowY: "auto" }}>
          {err && <div className="notice error">{err}</div>}
          {!data && !err && <div className="muted">Indlæser…</div>}
          {data && (
            <>
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
                <Pill status={ACCESS_PILL[data.profile.access.kind]?.[0]} label={ACCESS_PILL[data.profile.access.kind]?.[1]} />
                <span className="muted" style={{ fontSize: 14 }}>{data.profile.email}</span>
                {data.profile.phone && <span className="muted" style={{ fontSize: 14 }}>· {data.profile.phone}</span>}
                <span style={{ flex: 1 }} />
                <button className="btn small" data-testid="owner-profile-impersonate" onClick={() => onImpersonate(store)}>
                  <LogIn size={15} /> Log ind som butik
                </button>
                <button className="btn small" data-testid="owner-profile-manage" onClick={() => onManage(store)}>
                  Administrér
                </button>
              </div>

              <div className="prof-grid" data-testid="owner-profile-stats">
                <div className="prof-stat"><b>{data.stats.offers_live}</b><span>aktive tilbud</span></div>
                <div className="prof-stat"><b>{data.stats.orders_total}</b><span>ordrer i alt</span></div>
                <div className="prof-stat"><b>{data.stats.orders30}</b><span>ordrer 30d</span></div>
                <div className="prof-stat"><b>{data.stats.picked}</b><span>afhentet</span></div>
                <div className="prof-stat"><b>{data.stats.reserved}</b><span>reserveret nu</span></div>
                <div className="prof-stat"><b>{data.stats.customers}</b><span>kunder</span></div>
              </div>

              <div style={{ display: "grid", gap: 6, fontSize: 14 }}>
                <div><b>Facebook:</b> {data.profile.fb_connected ? (data.profile.fb_page_name || "Forbundet") : "Ikke forbundet"}</div>
                <div data-testid="od-legal">
                  <b>Vilkår/DPA:</b>{" "}
                  {data.profile.terms_accepted_at
                    ? `${data.profile.legal_current ? "Accepteret" : "Ældre version"} ${new Date(data.profile.terms_accepted_at).toLocaleDateString("da-DK")} (v. ${data.profile.dpa_version})`
                    : "Ikke accepteret endnu"}
                </div>
                <div><b>Oprettet:</b> {data.profile.created_at ? fmtShortDate(data.profile.created_at) : "–"} · <b>Sidst aktiv:</b> {data.profile.last_login_at ? fmtShortDate(data.profile.last_login_at) : "–"}</div>
                {data.profile.admin_note && <div className="muted">Notat: “{data.profile.admin_note}”</div>}
              </div>

              <div>
                <label style={{ fontWeight: 700, fontSize: 14, display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
                  <Receipt size={15} /> Faktureringsoplysninger
                </label>
                <BillingInfo info={data.profile.billing_info} pending={data.profile.invoice_pending} />
              </div>

              <div>
                <label style={{ fontWeight: 700, fontSize: 14, display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
                  <FileText size={15} /> Betalingshistorik
                </label>
                {data.billing.length ? (
                  <div data-testid="owner-profile-billing">
                    {data.billing.map((b) => (
                      <div className="bill-row" key={b.id}>
                        <span>{HISTORY_LABEL[b.kind] || b.kind}</span>
                        <span>{kr(b.amount_dkk)}</span>
                        <span className="muted">{fmtDateTime(b.created_at)}</span>
                      </div>
                    ))}
                  </div>
                ) : <div className="muted" style={{ fontSize: 14 }}>Ingen fakturaer/betalinger registreret endnu.</div>}
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                <div>
                  <label style={{ fontWeight: 700, fontSize: 14, display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
                    <Package size={15} /> Seneste tilbud
                  </label>
                  {data.recent_offers.length ? data.recent_offers.map((o) => (
                    <div className="bill-row" key={o.id}>
                      <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{o.name || "(uden navn)"}</span>
                      <span className="muted">{o.status}</span>
                    </div>
                  )) : <div className="muted" style={{ fontSize: 14 }}>Ingen tilbud.</div>}
                </div>
                <div>
                  <label style={{ fontWeight: 700, fontSize: 14, display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
                    <Activity size={15} /> Seneste aktivitet
                  </label>
                  {data.events.length ? data.events.map((e) => (
                    <div className="bill-row" key={e.id}>
                      <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}><EventText ev={e} /></span>
                    </div>
                  )) : <div className="muted" style={{ fontSize: 14 }}>Ingen aktivitet.</div>}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function ManageDialog({ store, onClose, onDone, onImpersonate }) {
  const flash = useFlash();
  const [busy, setBusy] = useState(false);
  const [temp, setTemp] = useState("");
  const [note, setNote] = useState(store.admin_note || "");
  const [followUp, setFollowUp] = useState(store.follow_up_at ? store.follow_up_at.slice(0, 10) : "");
  const [trialDate, setTrialDate] = useState("");
  const extend = (body) => call(() => api.post(`/owner/stores/${store.id}/trial`, body));

  const call = async (fn, successReload = true) => {
    setBusy(true);
    try {
      const r = await fn();
      if (r?.data?.flash) flash(r.data.flash);
      if (r?.data?.password) setTemp(r.data.password);
      if (successReload) onDone();
    } catch (err) {
      flash(apiError(err.response?.data?.detail) || err.message, "error");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="ck-overlay" role="dialog" aria-modal="true" data-testid="owner-manage-dialog">
      <div className="ck-modal" style={{ maxWidth: 520 }}>
        <div className="ck-head">
          <div className="ck-brand"><b>{store.name}</b></div>
          <button className="ck-close" onClick={onClose} aria-label="Luk" data-testid="owner-dialog-close">
            <X size={18} />
          </button>
        </div>
        <div className="ck-body" style={{ display: "grid", gap: 18, maxHeight: "72vh", overflowY: "auto" }}>
          <div className="muted" style={{ fontSize: 14 }}>{store.email}</div>

          <div>
            <label style={{ fontWeight: 700, fontSize: 14 }}>Plan</label>
            <div className="seg-row" style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 8 }}>
              {["free", "trial", "active", "past_due"].map((p) => {
                const [, label] = ACCESS_PILL[p];
                const activeNow = store.access.kind === p;
                return (
                  <button
                    key={p}
                    className={"btn small" + (activeNow ? " jatak" : "")}
                    disabled={busy}
                    data-testid={`owner-plan-${p}`}
                    onClick={() => call(() => api.post(`/owner/stores/${store.id}/plan`, { plan: p }))}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          </div>

          <div style={{ display: "grid", gap: 10 }}>
            <label style={{ fontWeight: 700, fontSize: 14 }}>Fakturaer</label>
            <OwnerInvoices store={store} onChanged={onDone} />
            {store.invoice_paid_at && (
              <small className="muted">Senest betalt: {fmtShortDate(store.invoice_paid_at)}{store.current_period_end ? ` · betalt periode til ${fmtShortDate(store.current_period_end)}` : ""}</small>
            )}
            <label style={{ fontWeight: 700, fontSize: 14, marginTop: 6 }}>Faktureringsoplysninger</label>
            <BillingInfo info={store.billing_info} pending={store.invoice_pending} />
          </div>

          <div style={{ display: "grid", gap: 10 }} data-testid="owner-trial-section">
            <label style={{ fontWeight: 700, fontSize: 14 }}>Forlæng prøveperiode</label>
            <small className="muted" data-testid="owner-trial-ends">
              Prøveperioden {store.trial_ends_at && new Date(store.trial_ends_at) < new Date() ? "udløb" : "udløber"}: {store.trial_ends_at ? fmtDateTime(store.trial_ends_at) : "–"}
              {store.locked && <> · <b style={{ color: "var(--red)" }}>Adgang låst</b></>}
            </small>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
              <button className="btn small" disabled={busy} onClick={() => extend({ days: 7 })} data-testid="owner-trial-7">
                <CalendarPlus size={15} /> +7 dage
              </button>
              <button className="btn small" disabled={busy} onClick={() => extend({ days: 14 })} data-testid="owner-trial-14">
                <CalendarPlus size={15} /> +14 dage
              </button>
              <input type="date" style={{ maxWidth: 170 }} value={trialDate} onChange={(e) => setTrialDate(e.target.value)} data-testid="owner-trial-date" />
              <button className="btn small" disabled={busy || !trialDate} onClick={() => extend({ until: trialDate })} data-testid="owner-trial-date-save">
                Sæt dato
              </button>
            </div>
          </div>

          <div style={{ display: "grid", gap: 10 }}>
            <label style={{ fontWeight: 700, fontSize: 14 }}>Opfølgning</label>
            <textarea rows={2} placeholder="Internt notat (fx aftale, kontaktperson, status)…"
              value={note} onChange={(e) => setNote(e.target.value)} data-testid="owner-note-input" />
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
              <input type="date" style={{ maxWidth: 190 }} value={followUp}
                onChange={(e) => setFollowUp(e.target.value)} data-testid="owner-followup-input" />
              <button className="btn small" disabled={busy} data-testid="owner-note-save"
                onClick={() => call(() => api.post(`/owner/stores/${store.id}/note`, { note, follow_up_at: followUp || null }))}>
                <StickyNote size={15} /> Gem notat
              </button>
            </div>
          </div>

          <div style={{ display: "grid", gap: 10 }}>
            <label style={{ fontWeight: 700, fontSize: 14 }}>Konto</label>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <button className="btn small" disabled={busy} data-testid="owner-impersonate"
                onClick={() => onImpersonate(store)}>
                <LogIn size={15} /> Log ind som butik
              </button>
              <button className="btn small" disabled={busy} data-testid="owner-reset-password"
                onClick={() => call(() => api.post(`/owner/stores/${store.id}/password`))}>
                <KeyRound size={15} /> Nulstil adgangskode
              </button>
              <button className="btn small" disabled={busy} data-testid="owner-suspend-toggle"
                onClick={() => call(() => api.post(`/owner/stores/${store.id}/suspend`, { suspended: !store.suspended }))}>
                <Ban size={15} /> {store.suspended ? "Genaktivér" : "Suspendér"}
              </button>
            </div>
            {temp && (
              <div className="banner" data-testid="owner-temp-password" style={{ borderRadius: 8 }}>
                Ny midlertidig adgangskode: <b>{temp}</b> — send den til butikken.
              </div>
            )}
          </div>

          <div style={{ borderTop: "1px solid var(--line)", paddingTop: 14 }}>
            <button className="btn small" style={{ color: "var(--red)", borderColor: "var(--red)" }} disabled={busy}
              data-testid="owner-delete"
              onClick={() => {
                if (window.confirm(`Slet "${store.name}" og alle butikkens data permanent?`))
                  call(() => api.delete(`/owner/stores/${store.id}`)).then(onClose);
              }}>
              <Trash2 size={15} /> Slet butik permanent
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Owner() {
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [manage, setManage] = useState(null);
  const [errorsFor, setErrorsFor] = useState(null);
  const [profileFor, setProfileFor] = useState(null);
  const flash = useFlash();
  const { impersonate } = useAuth();
  const navigate = useNavigate();

  const load = () =>
    api.get("/owner").then((r) => setD(r.data)).catch(() => setErr("Kunne ikke hente data."));
  useEffect(() => { load(); }, []);

  const doImpersonate = async (s) => {
    try {
      const r = await api.post(`/owner/stores/${s.id}/impersonate`);
      impersonate(r.data.token, r.data.store);
      if (r?.data?.flash) flash(r.data.flash);
      navigate("/overblik");
    } catch (e) {
      flash(apiError(e.response?.data?.detail) || e.message, "error");
    }
  };

  const filtered = useMemo(() => {
    if (!d) return [];
    const term = q.trim().toLowerCase();
    return d.stores.filter((s) => {
      if (filter === "unpaid" ? !s.inv_unpaid : filter === "pending" ? !s.invoice_pending : filter !== "all" && s.access.kind !== filter) return false;
      if (term && !`${s.name} ${s.email}`.toLowerCase().includes(term)) return false;
      return true;
    });
  }, [d, q, filter]);

  const attention = useMemo(() => {
    if (!d) return [];
    const ageOf = (s) => (s.access.kind === "past_due" && s.invoice_sent_at ? daysSince(s.invoice_sent_at) : -1);
    return d.stores
      .filter((s) => s.access.kind === "past_due" || s.follow_up_at || s.invoice_pending || s.inv_overdue || s.locked)
      .sort((a, b) => {
        const da = ageOf(a), db = ageOf(b);
        if (da !== db) return db - da; // ældste ubetalte faktura først
        return (a.follow_up_at || "9999").localeCompare(b.follow_up_at || "9999");
      });
  }, [d]);

  if (err) return <div className="loading">{err}</div>;
  if (!d) return <div className="loading">Indlæser…</div>;
  const t = d.totals || {};
  const todayStr = new Date().toISOString().slice(0, 10);

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Admin – Butikker</h1>
          <p>Platform-overblik og brugerstyring – kun synlig for admin.</p>
        </div>
      </div>

      <OwnerSupport />
      <SellerSettings onSaved={load} />
      <LandingSettings />
      <OwnerTestimonials />

      <section className="okpi-grid" data-testid="owner-kpis">
        {KPIS.map(([key, label, Icon, color]) => (
          <div
            className={"okpi" + (KPI_FILTER[key] && (t[key] ?? 0) > 0 ? " clickable" : "")}
            key={key}
            data-testid={`owner-kpi-${key}`}
            onClick={KPI_FILTER[key] && (t[key] ?? 0) > 0 ? () => setFilter(KPI_FILTER[key]) : undefined}
          >
            <span className="ico" style={iconTint(color)}><Icon size={20} /></span>
            <div>
              <b style={{ color: (key === "past_due" || key === "suspended" || key === "fb_broken" || key === "locked" || key === "overdue") && (t[key] ?? 0) > 0 ? "var(--red)" : undefined }}>
                {(t[key] ?? 0).toLocaleString("da-DK")}
              </b>
              <span>{label}</span>
            </div>
          </div>
        ))}
      </section>

      <section className="okpi-grid" data-testid="owner-revenue">
        <div className="okpi" data-testid="owner-kpi-mrr">
          <span className="ico" style={iconTint("var(--green)")}><DollarSign size={20} /></span>
          <div><b>{(t.mrr ?? 0).toLocaleString("da-DK")} kr.</b><span>MRR (pr. måned)</span></div>
        </div>
        <div className="okpi">
          <span className="ico" style={iconTint("var(--green)")}><TrendingUp size={20} /></span>
          <div><b>{(t.arr ?? 0).toLocaleString("da-DK")} kr.</b><span>ARR (pr. år)</span></div>
        </div>
        <div className="okpi">
          <span className="ico" style={iconTint("var(--amber)")}><Tag size={20} /></span>
          <div><b>{(t.price_monthly ?? 0).toLocaleString("da-DK")} kr.</b><span>pris pr. abonnement</span></div>
        </div>
        <div className="okpi" style={{ gridColumn: "span 1" }}>
          <span className="ico" style={iconTint("var(--green)")}><ShoppingBag size={20} /></span>
          <div><b>{(t.orders ?? 0).toLocaleString("da-DK")}</b><span>ordrer i alt · 14 dage</span></div>
          <Sparkline data={t.trend} color="var(--green)" />
        </div>
        <div className="okpi" data-testid="owner-kpi-errors">
          <span className="ico" style={iconTint((t.errors7d ?? 0) > 0 ? "var(--red)" : "var(--muted)")}><AlertTriangle size={20} /></span>
          <div>
            <b style={{ color: (t.errors7d ?? 0) > 0 ? "var(--red)" : undefined }}>{(t.errors7d ?? 0).toLocaleString("da-DK")}</b>
            <span>fejl (7 dage)</span>
          </div>
        </div>
      </section>

      {attention.length > 0 && (
        <section className="panel" data-testid="owner-attention">
          <h2 style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <Bell size={18} style={{ color: "var(--red)" }} /> Kræver opfølgning
            {(t.unpaid ?? 0) > 0 && (
              <span className="muted" style={{ fontWeight: 500, fontSize: 14 }} data-testid="owner-attention-total">
                · {t.unpaid ?? 0} ubetalte fakturaer i alt{" "}
                <b style={{ color: "var(--red)" }}>{((t.unpaid_amount_ore ?? 0) / 100).toLocaleString("da-DK", { minimumFractionDigits: 2 })} kr.</b>
              </span>
            )}
          </h2>
          {attention.map((s) => {
            const overdue = s.follow_up_at && s.follow_up_at.slice(0, 10) <= todayStr;
            const unpaidDays = s.access.kind === "past_due" && s.invoice_sent_at ? daysSince(s.invoice_sent_at) : null;
            return (
              <div className={"attn-row" + (overdue || s.access.kind === "past_due" ? " overdue" : "")} key={s.id} data-testid={`owner-attn-${s.id}`}>
                <div>
                  <div className="who">{s.name} <span className="muted" style={{ fontWeight: 400 }}>· {s.email}</span></div>
                  <div className="meta">
                    <Pill status={ACCESS_PILL[s.access.kind]?.[0]} label={ACCESS_PILL[s.access.kind]?.[1]} />
                    {unpaidDays !== null && (
                      <span className="pill no_show" style={{ fontWeight: 700 }} data-testid={`owner-attn-age-${s.id}`}>
                        <Clock size={12} style={{ verticalAlign: -2, marginRight: 3 }} />
                        {agingLabel(unpaidDays)} ubetalt
                      </span>
                    )}
                    {s.invoice_pending && <span className="pill pending" data-testid={`owner-attn-pending-${s.id}`}><FileText size={12} style={{ verticalAlign: -2, marginRight: 3 }} />Faktura på vej</span>}
                    {s.inv_unpaid > 0 && <span className={"pill " + (s.inv_overdue ? "no_show" : "pending")} data-testid={`owner-attn-unpaid-${s.id}`}>{s.inv_unpaid} ubetalt{s.inv_overdue ? " · forfalden" : ""}</span>}
                    {s.locked && <span className="pill no_show"><Lock size={12} style={{ verticalAlign: -2, marginRight: 3 }} />Låst</span>}
                    {s.invoice_sent_at && <span><Receipt size={13} style={{ verticalAlign: -2 }} /> Faktura sendt {fmtShortDate(s.invoice_sent_at)}</span>}
                    {s.follow_up_at && (
                      <span style={{ color: overdue ? "var(--red)" : undefined, fontWeight: overdue ? 700 : 400 }}>
                        <CalendarClock size={13} style={{ verticalAlign: -2 }} /> Opfølgning {fmtShortDate(s.follow_up_at)}
                      </span>
                    )}
                  </div>
                  {s.admin_note && <div className="note">“{s.admin_note}”</div>}
                </div>
                <div className="acts">
                  <button className="btn small" onClick={() => setManage(s)} data-testid={`owner-attn-manage-${s.id}`}>
                    Administrér <ChevronRight size={15} />
                  </button>
                </div>
              </div>
            );
          })}
        </section>
      )}

      <section className="panel">
        <div className="owner-toolbar" style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center", marginBottom: 14 }}>
          <div className="search-box" style={{ position: "relative", flex: "1 1 220px" }}>
            <Search size={16} style={{ position: "absolute", left: 10, top: 10, color: "var(--muted)" }} />
            <input
              style={{ width: "100%", paddingLeft: 32 }}
              placeholder="Søg butik eller email…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              data-testid="owner-search"
            />
          </div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {FILTERS.map(([k, label]) => (
              <button
                key={k}
                className={"btn small" + (filter === k ? " jatak" : "")}
                onClick={() => setFilter(k)}
                data-testid={`owner-filter-${k}`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        <div className="table-wrap">
          <table data-testid="owner-stores-table">
            <thead>
              <tr>
                <th>Butik</th>
                <th>E-mail</th>
                <th>Status</th>
                <th>Facebook</th>
                <th className="num">Tilbud</th>
                <th className="num">Ordrer 30d</th>
                <th className="num">Fejl 7d</th>
                <th className="num" title="Inkluderede / ekstra SMS denne måned">SMS md.</th>
                <th>Sidst aktiv</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((s) => {
                const [cls, label] = ACCESS_PILL[s.access.kind] || ["cancelled", s.access.kind];
                return (
                  <tr key={s.id} className="owner-row" data-testid={`owner-store-row-${s.id}`}>
                    <td>
                      <button className="linklike" onClick={() => setProfileFor(s)} data-testid={`owner-store-name-${s.id}`}
                        style={{ background: "none", border: 0, padding: 0, font: "inherit", fontWeight: 700, color: "var(--green-ink)", cursor: "pointer", textAlign: "left" }}>
                        {s.name}
                      </button>
                      {s.group_size > 1 && (
                        <div><small className="pill" data-testid={`owner-store-group-${s.id}`}>Gruppe #{s.group_id} · {s.group_size} butikker{s.is_primary ? " · fakturamodtager" : ""}</small></div>
                      )}
                      {s.billing_info_needed && <div><small className="pill warn" data-testid={`owner-store-billing-needed-${s.id}`}>Udfyld fakturaoplysninger</small></div>}
                    </td>
                    <td>{s.email || <span className="muted">(intet eget login)</span>}</td>
                    <td>
                      <span style={{ display: "inline-flex", gap: 4, flexWrap: "wrap" }}>
                        <Pill status={cls} label={label} />
                        {s.invoice_pending && <span data-testid={`owner-pending-${s.id}`}><Pill status="pending" label="Faktura på vej" /></span>}
                        {s.inv_unpaid > 0 && <span data-testid={`owner-unpaid-${s.id}`}><Pill status={s.inv_overdue ? "no_show" : "pending"} label={s.inv_overdue ? "Forfalden" : "Ubetalt"} /></span>}
                        {s.locked && <span data-testid={`owner-locked-${s.id}`}><Pill status="no_show" label="Låst" /></span>}
                      </span>
                    </td>
                    <td>
                      {s.fb_broken ? (
                        <span data-testid={`owner-fb-broken-${s.id}`}>{s.fb_page_name} <Pill status="cancelled" label="Afbrudt" /></span>
                      ) : s.fb_connected ? (
                        s.fb_page_name || <Pill status="picked_up" label="Forbundet" />
                      ) : (
                        <span className="muted">Ikke forbundet</span>
                      )}
                    </td>
                    <td className="num">{s.offers}</td>
                    <td className="num">{s.orders30}</td>
                    <td className="num clickable-cell" onClick={() => setErrorsFor(s)} data-testid={`owner-errors-cell-${s.id}`} title="Se fejl & aktivitet">
                      {s.errors7d > 0 ? <Pill status="no_show" label={String(s.errors7d)} /> : <span className="muted">0</span>}
                    </td>
                    <td className="num" data-testid={`owner-sms-${s.id}`}>
                      <small>{s.sms_month.included_used}/{s.sms_month.included_limit}{s.sms_month.extra_used > 0 && <> <b>+{s.sms_month.extra_used}</b></>}</small>
                    </td>
                    <td><small>{s.last_login_at ? fmtShortDate(s.last_login_at) : "–"}</small></td>
                    <td>
                      <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
                        <button className="btn small" onClick={() => setProfileFor(s)} data-testid={`owner-profile-${s.id}`}>
                          Profil
                        </button>
                        <button className="btn small" onClick={() => setManage(s)} data-testid={`owner-manage-${s.id}`}>
                          Administrér
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={10} className="muted" style={{ textAlign: "center", padding: "2rem" }}>
                    Ingen butikker matcher.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      {manage && (
        <ManageDialog
          store={manage}
          onImpersonate={doImpersonate}
          onClose={() => setManage(null)}
          onDone={async () => {
            const r = await api.get("/owner");
            setD(r.data);
            setManage((m) => (m ? r.data.stores.find((x) => x.id === m.id) || m : m));
          }}
        />
      )}

      {profileFor && (
        <StoreProfileDialog
          store={profileFor}
          onClose={() => setProfileFor(null)}
          onImpersonate={doImpersonate}
          onManage={(s) => { setProfileFor(null); setManage(s); }}
        />
      )}

      {errorsFor && <ErrorsDialog store={errorsFor} onClose={() => setErrorsFor(null)} />}
    </>
  );
}
